import { Step3AresProps, Step3FormProps, WizzardStep3Form } from '../types'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useEffect } from 'react'
import { DefaultValue } from '../constants'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { ActivationObject } from '../../../providers/ActivationObjectProvider/types'
import { VALIDATION_REQUIRED } from '../../../../constants/validation'
import { DynamicRequestBody } from '../../../../api/dynamicSites/types'
import { useDynamicDataMutation } from '../../../../api/dynamicSites/hooks'

const useStep3Form = (
    ares: Step3AresProps,
    activationObject?: ActivationObject,
): Step3FormProps => {
    const propName = (name: keyof WizzardStep3Form): string => name
    const isPhysical = activationObject?.personType === 'Physical'

    const { mutate: mutateDynamicData } = useDynamicDataMutation()

    const validationSchema = React.useMemo(
        () =>
            yup.object().shape({
                [propName('ico')]: !isPhysical
                    ? yup
                          .string()
                          .required(VALIDATION_REQUIRED)
                          .matches(
                              /^[0-9]+$/,
                              'Pole může obsahovat pouze čísla.',
                          )
                          .min(8, 'Pole musí obsahovat prěsně 8 znaků')
                          .max(8, 'Pole musí obsahovat prěsně 8 znaků')
                          .test(
                              'aresloaded',
                              'Nenalezna odpovídající společnost v registru ARES',
                              (value, context) => {
                                  return (
                                      !ares.isLoading &&
                                      ares.companyData !== undefined &&
                                      ares.companyData.ic === value
                                  )
                              },
                          )
                    : yup.string(),
                [propName('vat')]: !isPhysical
                    ? yup
                          .string()
                          .matches(
                              /^$|^CZ[0-9]{8,10}$/,
                              'Povolený formát CZ a 8-10 číslic',
                          )
                    : yup.string(),
                [propName('invoiceAddressName')]: isPhysical
                    ? yup.string().required(VALIDATION_REQUIRED)
                    : yup.string(),
                [propName('invoiceAddressZIP')]: isPhysical
                    ? yup
                          .string()
                          .required(VALIDATION_REQUIRED)
                          .matches(/^[0-9]/, 'Pole může obsahovat pouze čísla')
                          .min(5, 'Pole musí obsahovat 5 znaků')
                          .max(5, 'Pole musí obsahovat 5 znaků')
                    : yup.string(),
                [propName('invoiceAddressCity')]: isPhysical
                    ? yup.string().required(VALIDATION_REQUIRED)
                    : yup.string(),
                [propName('invoiceAddressStreet')]: isPhysical
                    ? yup.string().required(VALIDATION_REQUIRED)
                    : yup.string(),
                [propName('invoiceAddressCodeNumber')]: isPhysical
                    ? yup
                          .string()
                          .required(VALIDATION_REQUIRED)
                          .test(
                              'noslash',
                              'Pole nemůže obsahovat znak /',
                              (value) => {
                                  return (
                                      value === undefined ||
                                      value.indexOf('/') === -1
                                  )
                              },
                          )
                    : yup.string(),
                [propName('invoiceAddressGuidanceNumber')]: yup.string(),
                [propName('emailInvoice')]: isPhysical
                    ? yup
                          .string()
                          .required(VALIDATION_REQUIRED)
                          .matches(
                              /^([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[;]?[ ]?){1,3}$/,
                              'Zadejte maximálně 3 e-mailové adresy, oddělené středníkem',
                          )
                    : yup.string(),
            }),
        [ares.companyData, ares.isLoading, isPhysical],
    )

    const form = useForm<WizzardStep3Form>({
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: DefaultValue,
    })

    const emailValue = form.watch('emailInvoice')

    useEffect(() => {
        const handler = setTimeout(() => {
            if (emailValue) {
                // Remove spaces that are not following a semicolon and normalize semicolons
                let spaceNormalizedEmails = emailValue.replace(/(?!;)\s+/g, '')
                let normalizedEmails = spaceNormalizedEmails.replace(
                    /;\s*/g,
                    '; ',
                )

                // Split, trim, and filter out any empty strings
                const emailArray = normalizedEmails
                    .split(';')
                    .map((email) => email.trim())
                    .filter(Boolean)

                // Restrict to the first 3 emails and rejoin with proper formatting
                if (emailArray.length > 3) {
                    normalizedEmails = emailArray.slice(0, 3).join('; ')
                } else {
                    normalizedEmails = emailArray.join('; ')
                }

                // Update the form field only if changes were made
                if (emailValue !== normalizedEmails) {
                    form.setValue('emailInvoice', normalizedEmails)
                }
            }
        }, 700) // 700ms debounce time

        // Cleanup function to clear the timeout if the component unmounts
        return () => clearTimeout(handler)
    }, [emailValue, form])

    const reset = form.reset
    React.useEffect(() => {
        if (activationObject) {
            reset({
                ico: activationObject.ico ?? DefaultValue.ico,
                vat: activationObject.vat ?? DefaultValue.vat,
                emailInvoice:
                    activationObject.emailInvoice ??
                    activationObject.personEmail ??
                    DefaultValue.emailInvoice,
                emailSendingDocumentation:
                    activationObject.emailSendingDocumentation ??
                    DefaultValue.emailSendingDocumentation,
                invoiceAddressName:
                    activationObject.invoiceAddressName ??
                    DefaultValue.invoiceAddressName,
                invoiceAddressZIP:
                    activationObject.invoiceAddressZIP ??
                    DefaultValue.invoiceAddressZIP,
                invoiceAddressCity:
                    activationObject.invoiceAddressCity ??
                    DefaultValue.invoiceAddressCity,
                invoiceAddressStreet:
                    activationObject.invoiceAddressStreet ??
                    DefaultValue.invoiceAddressStreet,
                invoiceAddressGuidanceNumber:
                    activationObject.invoiceAddressGuidanceNumber ??
                    DefaultValue.invoiceAddressGuidanceNumber,
                invoiceAddressCodeNumber:
                    activationObject.invoiceAddressCodeNumber ??
                    DefaultValue.invoiceAddressCodeNumber,
            })
        }
    }, [activationObject, reset])

    const { update } = useActivationObject()
    const onSubmit = useCallback(
        (data: WizzardStep3Form) => {
            if (activationObject) {
                const {
                    actualWizardStep,
                    previousWizardStep,
                    ...restOfObject
                } = activationObject

                const newObject = {
                    ...restOfObject,
                    ...data,
                }
                update(newObject)
                const dynamicBody = {
                    synonym: activationObject.synonym,
                    mainSite: 'Summary',
                    type: 'Redirect',
                    dynamicSiteNumber: null,
                } as DynamicRequestBody

                mutateDynamicData(dynamicBody)
            }
        },
        [activationObject, mutateDynamicData, update],
    )

    const { setValue } = form
    const handleUseObjectAddress = useCallback(() => {
        if (activationObject) {
            setValue('invoiceAddressName', activationObject.personName || '')
            setValue('invoiceAddressZIP', activationObject.objectZIP || '')
            setValue('invoiceAddressCity', activationObject.objectCity || '')
            setValue(
                'invoiceAddressStreet',
                activationObject.objectStreet || '',
            )
            setValue(
                'invoiceAddressCodeNumber',
                activationObject.objectCodeNumber || '',
            )
            setValue(
                'invoiceAddressGuidanceNumber',
                activationObject.objectGuidanceNumber || '',
            )
        }
    }, [activationObject, setValue])

    return {
        form,
        onSubmit,
        handleUseObjectAddress,
    }
}

export default useStep3Form
